<template>
	<v-bottom-sheet v-model="sheet" content-class="rounded-t-lg">
		<v-form ref="form" @submit.prevent="submit">
			<v-card class="rounded-t-lg">
				<v-card-title class="justify-center text-h6">
					{{ $T('Visits.add-visit') }}
				</v-card-title>

				<v-card-text>
					<v-row dense>
						<v-col v-if="customersTypes.length" cols="12">
							<div class="text-body-2 mb-1">
								<required-label :text="$T('inputs.customer-type')" />
							</div>
							<v-text-field
								:value="customerTypeLabel"
								:rules="rules.required"
								readonly
								outlined
								dense
								hide-details="auto"
								append-icon="mdi-menu-down"
								@click="$refs.customerTypePicker.open(customersTypes, customerTypeId)"
							/>
							<item-picker-dialog
								ref="customerTypePicker"
								:title="$T('inputs.customer-type')"
								@select="onCustomerTypeSelected"
							/>
						</v-col>

						<v-col cols="12">
							<div class="text-body-2 mb-1">
								<required-label :text="$T('inputs.region')" />
							</div>
							<v-text-field
								:value="regionLabel"
								:rules="rules.required"
								readonly
								outlined
								dense
								hide-details="auto"
								clearable
								append-icon="mdi-menu-down"
								@click="$refs.regionPicker.open(regions, regionId)"
								@click:clear="onRegionSelected(null)"
							/>
							<item-picker-dialog
								ref="regionPicker"
								:title="$T('inputs.region')"
								@select="onRegionSelected"
							/>
						</v-col>

						<v-col cols="12">
							<div class="text-body-2 mb-1">
								<required-label :text="$T('headers.customer')" />
							</div>
							<v-text-field
								:value="entityName"
								:rules="rules.required"
								readonly
								outlined
								dense
								hide-details="auto"
								clearable
								append-icon="mdi-qrcode-scan"
								@click="$refs.entityPicker.openPaginated(fetchEntitiesPage, entityId)"
								@click:clear="onEntitySelected(null)"
								@click:append="openScanDialog"
							/>
							<item-picker-dialog
								ref="entityPicker"
								:title="$T('headers.customer')"
								@select="onEntitySelected"
							/>
						</v-col>
					</v-row>
				</v-card-text>

				<v-card-actions class="add-visit-bottom-sheet__actions pb-4">
					<v-btn color="primary" rounded block large type="submit">
						{{ $T('next') }}
					</v-btn>
					<v-btn outlined rounded block large class="mt-2 mx-0" @click="sheet = false">
						{{ $T('cancel') }}
					</v-btn>
				</v-card-actions>

				<v-progress-linear :value="33" height="3" color="primary" />
			</v-card>
		</v-form>

		<v-dialog v-model="scanDialog" max-width="560">
			<v-card>
				<v-toolbar flat color="primary" dark>
					<v-spacer />
					{{ $T('Visits.scan-qr') }}
					<v-spacer />
				</v-toolbar>

				<v-card-text class="pt-4">
					<qrcode-stream
						v-if="scanDialog"
						@decode="onQrDecode"
						@init="onQrInit"
					/>
					<p v-if="scanError" class="error--text mt-3 mb-0">{{ scanError }}</p>
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<v-btn text color="grey darken-1" @click="scanDialog = false">
						{{ $T('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-bottom-sheet>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { QrcodeStream } from 'vue-qrcode-reader'
import rules from '@/helpers/validation rules'
import { deletionExternal } from '@/configs/general.configs'
import RequiredLabel from '@/components/RequiredLabel.vue'
import ItemPickerDialog from '@/components/ItemPickerDialog.vue'

export default {
	name: 'AddVisitBottomSheet',

	components: {
		RequiredLabel,
		QrcodeStream,
		ItemPickerDialog
	},

	props: {
		// when true, submit only emits the selection instead of resetting the
		// draft and navigating — used to change the customer mid-flow
		skipReset: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			rules,
			moment,

			sheet: false,
			visitDate: null,
			customerTypeId: null,
			regionId: null,
			entityId: null,
			entityName: null,

			scanDialog: false,
			scanError: null,
			scanHandled: false
		}
	},

	watch: {
		scanDialog(val) {
			if (!val) {
				this.scanError = null
				this.scanHandled = false
			}
		}
	},

	computed: {
		...mapState({
			entities: state => state.entities.entities,
			regions: state => state.regions.regions,
			customersTypes: state => deletionExternal
				? state.entities.customersTypes.filter(c => c.name !== 'Externals')
				: state.entities.customersTypes
		}),

		customerTypeLabel() {
			const match = this.customersTypes.find(type => type.id === this.customerTypeId)
			return match ? match.name : null
		},

		regionLabel() {
			const match = this.regions.find(region => region.id === this.regionId)
			return match ? match.name : null
		}
	},

	methods: {
		onCustomerTypeSelected(value) {
			this.customerTypeId = value
			this.onCustomerTypeChange()
		},

		onCustomerTypeChange() {
			this.regionId = null
			this.onEntitySelected(null)
		},

		onRegionSelected(value) {
			this.regionId = value
			this.onRegionChange()
		},

		onRegionChange() {
			this.onEntitySelected(null)
		},

		onEntitySelected(value, item = null) {
			this.entityId = value
			this.entityName = item ? item.name : null
		},

		// entities can number in the tens of thousands, so the picker searches/paginates them on
		// the backend (entities/fetchAllData) instead of loading the full list into memory
		fetchEntitiesPage(query, perPage) {
			return this.$store.dispatch('entities/fetchAllData', {
				typeId: this.customerTypeId,
				regionIds: this.regionId ? [this.regionId] : [],
				name: query,
				perPage
			}).then(() => Array.from(this.entities))
		},

		openScanDialog() {
			this.scanError = null
			this.scanHandled = false
			this.scanDialog = true
		},

		onQrInit(promise) {
			promise.catch(() => {
				this.scanError = this.$T('Visits.qr-camera-error')
			})
		},

		onQrDecode(rawValue) {
			if (this.scanHandled) return
			this.scanError = null
			this.scanHandled = true

			this.$store.dispatch('entities/fetchQr', { qrCodeData: rawValue })
				.then(entity => {
					// a readable code that matches no customer is not the same as an unreadable one
					if (!entity) return Promise.reject(new Error('qr-customer-not-found'))

					return this.applyScannedEntity(entity)
				})
				.then(() => {
					this.scanDialog = false
					this.$eventBus.$emit('show-snackbar', this.$T('Visits.qr-applied'))
				})
				.catch(error => {
					this.scanHandled = false
					this.scanError = error && error.message === 'qr-customer-not-found'
						? this.$T('Visits.qr-customer-not-found')
						: this.$T('Visits.qr-invalid')
				})
		},

		// sets the type/region filters from the scanned customer, then applies it directly as the selection
		applyScannedEntity(entity) {
			if (this.customersTypes.length) {
				this.customerTypeId = this.customersTypes.some(type => type.id === entity.typeId) ? entity.typeId : null
			}

			if (this.regions.length) {
				this.regionId = this.regions.some(region => region.id === entity.regionId) ? entity.regionId : null
			}

			this.onEntitySelected(entity.id, entity)

			return Promise.resolve()
		},

		open(customerTypeId, date, { regionId = null, entityId = null, entityName = null } = {}) {
			if (this.$refs.form) this.$refs.form.resetValidation()

			this.customerTypeId = customerTypeId
			this.regionId = regionId
			// seed the known name so closing without re-picking doesn't blank it out
			this.onEntitySelected(entityId, entityName ? { name: entityName } : null)
			// the visit belongs to the day the user is standing on - how far back that may go
			// is already gated by lockVisitAfterDuration before the add button is even shown
			this.visitDate = date || moment().format('YYYY-MM-DD')
			this.sheet = true

			if (!this.customersTypes.length) {
				this.$store.dispatch('entities/fetchEntityType')
			}

			if (!this.regions.length) {
				this.$store.dispatch('regions/fetchAll')
			}
		},

		submit() {
			if (!this.$refs.form.validate()) return

			const selection = {
				customerTypeId: this.customerTypeId,
				entityId: this.entityId,
				// carried along so the details header can show the name straight away, without
				// waiting on (or depending on) the app-wide entities fetch
				entityName: this.entityName,
				visitDate: this.visitDate
			}

			if (this.skipReset) {
				this.sheet = false
				this.$emit('submit', selection)
				return
			}

			this.$store.dispatch('visitDraft/reset')
				.then(() => this.$store.dispatch('visitDraft/setCustomer', selection))
				.then(() => {
					this.sheet = false
					this.$router.push({
						name: 'add-visit-details',
						// ids only - the name lives in the draft, and a reload rebuilds it from the store
						query: {
							customerTypeId: selection.customerTypeId,
							entityId: selection.entityId,
							visitDate: selection.visitDate
						}
					})
				})
		}
	}
}
</script>

<style scoped>
.add-visit-bottom-sheet__actions {
	display: flex;
	flex-direction: column;
	align-items: stretch;
}
</style>
